function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import { useHistory } from 'react-router-dom';
import compact from 'lodash/compact';
import { TableList, Typography, TableListActions, DropdownMenu } from 'seagull';
import { getAgreementPdfUrl } from "../../business-logic/relationshipLogic";
import { LinkIcon, EyeLine, EditLine, EmailSent, Download, DocumentCopy, TrashLiner24, ActivityLiner24 } from 'seagull/icons';
import { useOverlayContext, useIoContext, useStore } from "../../contexts";
import { Intent } from "../../infra/access-control";
import { ClientPreview } from "../../components/ClientPreview/ClientPreview";
import { getStatusUIData, getRowAndMainAction } from "./ProposalsDashboard.utils";
import { relationshipSummaryModel, timeModel } from "../../models";
import { selectors } from "../../store/state-selectors";
import { isExpired } from "../../models/relationship-summary";
export default function ProposalsDashboardRow(props) {
  const {
    proposal,
    isVendor,
    index,
    refresh,
    onViewActivityLog,
    onViewProposal,
    proposalActions
  } = props;
  const expired = isExpired(proposal);
  const history = useHistory();
  const overlayContext = useOverlayContext();
  const {
    openNewTab
  } = useIoContext();
  const heapId = proposal.isSample ? 'test-proposal' : 'proposals';
  const pdfHost = useStore(selectors.pdfHost);
  const opposingPartyName = relationshipSummaryModel.opposingPartyName(proposal, isVendor);
  const {
    statusText,
    statusSkin,
    lastUpdateText
  } = getStatusUIData(proposal, isVendor);
  const sellerFullName = relationshipSummaryModel.sellerFullName(proposal);
  const {
    getCancelProposalAction,
    getCopyProposalLinkAction,
    getDuplicateProposalAction,
    getEditProposalAction,
    getSendProposalReminderAction
  } = proposalActions;
  const {
    rowProps,
    mainAction
  } = getRowAndMainAction({
    proposal,
    isVendor,
    heapId,
    history,
    openClientPreview
  });
  const onDownloadPDF = proposal => {
    openNewTab(getAgreementPdfUrl(proposal.id, pdfHost));
  };
  function renderSendProposalReminderAction() {
    const {
      intent,
      disabled,
      text,
      onClick
    } = getSendProposalReminderAction(proposal.id, proposal.isSample);
    return /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      intent: intent,
      disabled: disabled,
      onClick: onClick,
      iconStart: /*#__PURE__*/React.createElement(EmailSent, null),
      "data-testid": "proposals-row-send-reminder",
      key: "reminder".concat(proposal.id)
    }, text);
  }
  function renderCopyProposalLinkAction() {
    const {
      intent,
      text,
      onClick
    } = getCopyProposalLinkAction(proposal);
    return /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      intent: intent,
      onClick: onClick,
      iconStart: /*#__PURE__*/React.createElement(LinkIcon, null),
      "data-testid": proposal.isSample ? 'test-proposal-copy-link' : 'proposals-row-copy-link',
      key: "copy-link-".concat(proposal.id)
    }, text);
  }
  function renderDuplicateProposalAction() {
    const {
      intent,
      disabled,
      text,
      onClick
    } = getDuplicateProposalAction(proposal.id, proposal.isSample);
    return /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      intent: intent,
      iconStart: /*#__PURE__*/React.createElement(DocumentCopy, null),
      disabled: disabled,
      "data-testid": "proposals-row-duplicate",
      onClick: onClick,
      key: "duplicate".concat(proposal.id)
    }, text);
  }
  function renderEditProposalAction() {
    const {
      text,
      intent,
      disabled,
      onClick
    } = getEditProposalAction(proposal, 'proposalsDashboard');
    return /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      intent: intent,
      onClick: onClick,
      iconStart: /*#__PURE__*/React.createElement(EditLine, null),
      disabled: disabled,
      "data-testid": "proposals-row-withdraw-to-edit",
      key: "withdraw-to-edit".concat(proposal.id)
    }, text);
  }
  function renderCancelProposalAction() {
    const {
      text,
      intent,
      onClick
    } = getCancelProposalAction(proposal.id, refresh);
    return /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      skin: "error",
      intent: intent,
      iconStart: /*#__PURE__*/React.createElement(TrashLiner24, null),
      "data-testid": proposal.isSample ? 'test-proposal-cancel' : 'proposals-row-cancel',
      onClick: onClick,
      key: "cancel".concat(proposal.id)
    }, text);
  }
  function openClientPreview() {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: props => /*#__PURE__*/React.createElement(ClientPreview, _extends({
        type: "relationshipId",
        relationshipId: proposal.id
      }, props))
    });
  }
  const actions = compact([isVendor && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    key: "view-proposal".concat(proposal.id),
    intent: Intent.VendorViewProposal,
    iconStart: /*#__PURE__*/React.createElement(EyeLine, null),
    "data-testid": "proposals-row-view-proposal",
    "data-heapid": "".concat(heapId, "-review-btn"),
    onClick: () => onViewProposal(proposal)
  }, "View"), isVendor && renderEditProposalAction(), isVendor && !expired && renderCopyProposalLinkAction(), isVendor && !expired && renderSendProposalReminderAction(), isVendor && renderDuplicateProposalAction(), isVendor && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    iconStart: /*#__PURE__*/React.createElement(ActivityLiner24, null),
    "data-testid": "view-activity-log",
    key: "activity".concat(proposal.id),
    onClick: () => onViewActivityLog(proposal)
  }, "View activity log"), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    intent: Intent.DownloadAgreementPdf,
    iconStart: /*#__PURE__*/React.createElement(Download, null),
    disabled: proposal.isSample,
    "data-testid": "proposals-row-download-pdf",
    onClick: () => onDownloadPDF(proposal),
    key: "dowload-pdf-".concat(proposal.id)
  }, "Download PDF"), isVendor && renderCancelProposalAction()]);
  return /*#__PURE__*/React.createElement(TableList.Row, _extends({
    key: proposal.id,
    intent: mainAction.intent
  }, rowProps, {
    rowId: proposal.id,
    items: [{
      value: /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        weight: "normal",
        "data-testid": "proposals-row-".concat(index, "-name")
      }, opposingPartyName, proposal.title && proposal.title !== opposingPartyName && /*#__PURE__*/React.createElement(Typography, {
        as: "span",
        variant: "paragraph2",
        skin: "secondary"
      }, "\xA0(", proposal.title, ")"))
    }, {
      value: /*#__PURE__*/React.createElement(Typography, {
        skin: "secondary"
      }, proposal.createdOn ? timeModel.formatDate(proposal.createdOn) : '')
    }, {
      value: sellerFullName != null && /*#__PURE__*/React.createElement(Typography, {
        skin: "secondary"
      }, sellerFullName)
    }, {
      value: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "row-".concat(index, "-status"),
        "data-skin": statusSkin,
        skin: statusSkin,
        weight: "normal"
      }, statusText), /*#__PURE__*/React.createElement(Typography, {
        variant: "paragraph2",
        size: "small",
        "data-testid": "row-".concat(index, "-status-update"),
        "data-skin": statusSkin,
        skin: statusSkin === 'error' ? 'error' : 'secondary'
      }, lastUpdateText))
    }],
    actionColumn: /*#__PURE__*/React.createElement(TableListActions, {
      buttonText: mainAction.buttonText,
      buttonIntent: mainAction.intent,
      testId: proposal.isSample ? 'test-proposal' : "proposals-row-".concat(index),
      btnHeapId: mainAction.btnHeapId,
      menuHeapId: "".concat(heapId, "-menu-btn"),
      onClickButton: mainAction.onClickButton
    }, actions.length > 0 && actions),
    "data-testid": proposal.isSample ? 'test-proposal' : "proposals-row-".concat(index),
    "data-heapid": heapId
  }));
}